import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { posts } from '../data/posts';
import SEO from './SEO';
import NotFound from './NotFound';
import { useTheme } from '../context/ThemeContext';

function BlogPost() {
  const { id } = useParams();
  const [showShareToast, setShowShareToast] = useState(false);
  const { isDark, toggleTheme, layoutMode, toggleLayout } = useTheme();
  const navigate = useNavigate();

  const goToRandomEssay = () => {
    const otherPosts = posts.filter(p => p.id !== id);
    const randomPost = otherPosts[Math.floor(Math.random() * otherPosts.length)];
    navigate(`/blog/${randomPost.id}`);
  };
  const post = posts.find(p => p.id === id);

  if (!post) {
    return <NotFound />;
  }

  const wordCount = post.content.trim().split(/\s+/).length;
  const readingTime = Math.ceil(wordCount / 200);

  const currentUrl = typeof window !== 'undefined' ? window.location.href : `https://keshavrao.com/blog/${id}`;
  
  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(currentUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Control circles component (shared across all layouts)
  const ControlCircles = () => (
    <div className="flex items-center gap-1.5">
      <button
        onClick={toggleTheme}
        className="w-5 h-5 rounded-full overflow-hidden relative group"
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        style={{ background: isDark ? '#fafaf9' : '#1c1917' }}
      >
        <motion.div
          className="absolute w-4 h-4 rounded-full"
          style={{ background: isDark ? '#c2410c' : '#fdba74', filter: 'blur(4px)' }}
          animate={{
            x: ['-10%', '50%', '20%', '40%', '-10%'],
            y: ['-10%', '30%', '60%', '10%', '-10%'],
            scale: [1, 1.3, 0.9, 1.2, 1],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute w-3 h-3 rounded-full"
          style={{ background: isDark ? '#ea580c' : '#fb923c', filter: 'blur(4px)' }}
          animate={{
            x: ['50%', '0%', '40%', '20%', '50%'],
            y: ['40%', '10%', '50%', '30%', '40%'],
            scale: [0.9, 1.2, 1, 1.3, 0.9],
          }}
          transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
        />
      </button>
      <button
        onClick={toggleLayout}
        className="w-5 h-5 rounded-full overflow-hidden relative group"
        aria-label="Change layout"
        style={{ background: isDark ? '#fafaf9' : '#1c1917' }}
      >
        <motion.div
          className="absolute w-4 h-4 rounded-full"
          style={{ background: isDark ? '#9a3412' : '#fed7aa', filter: 'blur(4px)' }}
          animate={{
            x: ['40%', '0%', '30%', '60%', '40%'],
            y: ['10%', '50%', '20%', '40%', '10%'],
            scale: [1, 0.9, 1.3, 1, 1],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute w-3 h-3 rounded-full"
          style={{ background: isDark ? '#f97316' : '#ffedd5', filter: 'blur(4px)' }}
          animate={{
            x: ['10%', '40%', '0%', '50%', '10%'],
            y: ['50%', '0%', '30%', '20%', '50%'],
            scale: [1.2, 1, 0.9, 1.3, 1.2],
          }}
          transition={{ duration: 13, repeat: Infinity, ease: 'easeInOut' }}
        />
      </button>
      <button
        onClick={goToRandomEssay}
        className="w-5 h-5 rounded-full overflow-hidden relative"
        aria-label="Random essay"
        style={{ background: isDark ? '#fafaf9' : '#1c1917' }}
      >
        <motion.div
          className="absolute w-4 h-4 rounded-full"
          style={{ background: isDark ? '#7c2d12' : '#fbbf24', filter: 'blur(4px)' }}
          animate={{
            x: ['20%', '50%', '0%', '40%', '20%'],
            y: ['30%', '10%', '50%', '20%', '30%'],
            scale: [0.9, 1.2, 1, 1.3, 0.9],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute w-3 h-3 rounded-full"
          style={{ background: isDark ? '#b45309' : '#fde68a', filter: 'blur(4px)' }}
          animate={{
            x: ['60%', '20%', '50%', '10%', '60%'],
            y: ['30%', '50%', '20%', '60%', '30%'],
            scale: [1, 1.2, 0.9, 1.1, 1],
          }}
          transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
        />
      </button>
    </div>
  );

  // Terminal Layout
  if (layoutMode === 'terminal') {
    return (
      <article 
        className="min-h-screen font-mono text-sm"
        style={{ background: '#0a0a0a', color: '#00ff41' }}
      >
        <SEO 
          title={post.title}
          description={post.description}
          article={true}
          canonical={`https://keshavrao.com/blog/${id}`}
          keywords={post.tags || []}
        />
        
        <div className="max-w-3xl mx-auto px-6 py-8">
          {/* Terminal Window */}
          <div 
            className="rounded-lg overflow-hidden"
            style={{ 
              background: '#0a0a0a',
              border: '1px solid #1a1a1a',
              boxShadow: '0 0 40px rgba(0, 255, 65, 0.03)'
            }}
          >
            {/* Title Bar */}
            <div className="flex items-center justify-between px-4 py-2" style={{ background: '#111', borderBottom: '1px solid #1a1a1a' }}>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ background: '#ff5f56' }}></div>
                <div className="w-3 h-3 rounded-full" style={{ background: '#ffbd2e' }}></div>
                <div className="w-3 h-3 rounded-full" style={{ background: '#27ca3f' }}></div>
              </div>
              <span className="text-xs" style={{ color: '#444' }}>cat {post.title.toLowerCase().replace(/\s+/g, '-')}.md</span>
              <ControlCircles />
            </div>

            {/* Terminal Content */}
            <div className="p-6">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                {/* Command */}
                <div className="mb-6" style={{ color: '#666' }}>
                  $ cat ./essays/{post.title.toLowerCase().replace(/\s+/g, '-')}.md
                </div>

                {/* File header */}
                <div className="mb-6 pb-4" style={{ borderBottom: '1px solid #1a1a1a' }}>
                  <div style={{ color: '#00ff41' }} className="text-lg mb-2">
                    # {post.title}
                  </div>
                  <div style={{ color: '#444' }} className="text-xs">
                    Created: {post.date} | {readingTime} min read | {wordCount} words
                  </div>
                </div>

                {/* Content */}
                <div 
                  className="leading-relaxed space-y-4"
                  style={{ color: '#00ff41', opacity: 0.8 }}
                >
                  {post.content.split('\n\n').map((paragraph, idx) => (
                    <motion.p 
                      key={idx}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.2, delay: idx * 0.05 }}
                      className="leading-relaxed"
                      style={{ 
                        color: paragraph.startsWith('#') ? '#00ff41' : 'rgba(0, 255, 65, 0.7)',
                        fontWeight: paragraph.startsWith('#') ? 'bold' : 'normal'
                      }}
                    >
                      {paragraph.replace(/^#+\s*/, paragraph.startsWith('#') ? '## ' : '')}
                    </motion.p>
                  ))}
                </div>

                {/* Footer */}
                <div className="mt-8 pt-4" style={{ borderTop: '1px solid #1a1a1a' }}>
                  <div className="flex items-center gap-4 mb-4" style={{ color: '#444' }}>
                    <Link to="/" className="hover:text-green-400 transition-colors">
                      $ cd ..
                    </Link>
                    <span>|</span>
                    <button onClick={copyToClipboard} className="hover:text-green-400 transition-colors">
                      $ cp link clipboard
                    </button>
                  </div>
                  <div className="flex items-center">
                    <span style={{ color: '#666' }}>$ </span>
                    <span className="animate-pulse" style={{ color: '#00ff41' }}>▋</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {showShareToast && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="fixed bottom-6 right-6 px-4 py-2 rounded text-sm"
              style={{ background: '#00ff41', color: '#0a0a0a' }}
            >
              Link copied to clipboard
            </motion.div>
          )}
        </div>
      </article>
    );
  }

  // macOS Layout
  if (layoutMode === 'macos') {
    const macBg = isDark ? '#1e1e1e' : '#ffffff';
    const macText = isDark ? '#ffffff' : '#000000';
    const macMuted = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)';
    const macBorder = isDark ? '#3d3d3d' : '#e5e5e5';
    const macToolbar = isDark ? '#2d2d2d' : '#f5f5f5';

    return (
      <article 
        className="min-h-screen p-8"
        style={{ background: isDark ? '#0f0f0f' : '#e8e8e8', fontFamily: 'SF Pro Text, -apple-system, sans-serif' }}
      >
        <SEO 
          title={post.title}
          description={post.description}
          article={true}
          canonical={`https://keshavrao.com/blog/${id}`}
          keywords={post.tags || []}
        />
        
        <div className="max-w-4xl mx-auto">
          {/* Window */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="rounded-xl overflow-hidden"
            style={{ 
              background: macBg,
              boxShadow: isDark 
                ? '0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255,255,255,0.1)' 
                : '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0,0,0,0.05)'
            }}
          >
            {/* Title Bar */}
            <div 
              className="flex items-center justify-between px-4 py-3"
              style={{ background: macToolbar, borderBottom: `1px solid ${macBorder}` }}
            >
              <div className="flex items-center gap-2">
                <Link to="/" className="w-3 h-3 rounded-full bg-[#ff5f57] hover:brightness-90"></Link>
                <div className="w-3 h-3 rounded-full bg-[#febc2e]"></div>
                <div className="w-3 h-3 rounded-full bg-[#28c840]"></div>
              </div>
              <span className="text-xs" style={{ color: macMuted }}>{post.title}</span>
              <ControlCircles />
            </div>

            {/* Content */}
            <div className="p-12">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <h1 
                  className="text-4xl font-semibold mb-4"
                  style={{ color: macText }}
                >
                  {post.title}
                </h1>
                <div className="text-sm mb-8" style={{ color: macMuted }}>
                  {post.date} · {readingTime} min read
                </div>

                <div 
                  className="prose prose-lg max-w-none"
                  style={{ color: macMuted, lineHeight: 1.8 }}
                >
                  <ReactMarkdown
                    components={{
                      p: ({ node, ...props }) => (
                        <p className="mb-6 leading-relaxed" style={{ color: macMuted }} {...props} />
                      ),
                      h2: ({ node, children, ...props }) => (
                        <h2 className="text-2xl font-semibold mt-10 mb-4" style={{ color: macText }} {...props}>{children}</h2>
                      ),
                      h3: ({ node, children, ...props }) => (
                        <h3 className="text-xl font-semibold mt-8 mb-3" style={{ color: macText }} {...props}>{children}</h3>
                      ),
                      strong: ({ node, ...props }) => (
                        <strong className="font-semibold" style={{ color: macText }} {...props} />
                      ),
                      a: ({ node, children, ...props }) => (
                        <a className="text-blue-500 hover:underline" target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
                      ),
                      code: ({ node, ...props }) => (
                        <code 
                          className="px-1.5 py-0.5 rounded text-sm font-mono"
                          style={{ background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}
                          {...props} 
                        />
                      ),
                    }}
                  >
                    {post.content}
                  </ReactMarkdown>
                </div>

                {/* Footer */}
                <div 
                  className="mt-12 pt-6 flex items-center gap-4"
                  style={{ borderTop: `1px solid ${macBorder}` }}
                >
                  <a href={shareLinks.twitter} target="_blank" rel="noopener noreferrer" className="text-sm hover:opacity-70" style={{ color: macMuted }}>
                    Share
                  </a>
                  <button onClick={copyToClipboard} className="text-sm hover:opacity-70" style={{ color: macMuted }}>
                    Copy Link
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {showShareToast && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="fixed bottom-6 right-6 bg-black text-white px-4 py-2 rounded-lg text-sm"
            >
              Link copied
            </motion.div>
          )}
        </div>
      </article>
    );
  }

  // Default Layout
  const bgColor = isDark ? '#0F0F0F' : '#FAF9F6';
  const textColor = isDark ? 'text-white' : 'text-stone-900';
  const textMuted = isDark ? 'text-white/60' : 'text-stone-700';
  const textSubtle = isDark ? 'text-white/40' : 'text-stone-500';
  const borderColor = isDark ? 'border-white/5' : 'border-stone-200';
  const linkHover = isDark ? 'hover:text-white/80' : 'hover:text-stone-900';

  return (
    <article className={`min-h-screen ${textColor} transition-colors duration-300`} style={{ fontFamily: '"Noto Sans", -apple-system, sans-serif', backgroundColor: bgColor }}>
      <SEO 
        title={post.title}
        description={post.description}
        article={true}
        canonical={`https://keshavrao.com/blog/${id}`}
        keywords={post.tags || []}
      />
      
      <div className="max-w-[75ch] mx-auto px-6 py-20">
        <motion.header
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-16"
        >
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className={`${textSubtle} ${linkHover} text-xs transition-colors`}>
              ← back
            </Link>
            <ControlCircles />
          </div>
          <h1 className={`text-5xl font-light tracking-tight mb-6 leading-[1.1] ${textColor}`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
            {post.title}
          </h1>
          <div className={`flex items-center gap-4 text-sm ${textSubtle}`}>
            <time dateTime={post.date} style={{ fontFeatureSettings: '"onum" 1' }}>
              {(() => {
                const dateParts = post.date.split('-');
                if (dateParts.length === 3) {
                  const [year, month, day] = dateParts;
                  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  return `${year} ${monthNames[parseInt(month) - 1]} ${parseInt(day)}`;
                } else {
                  const [year, month] = dateParts;
                  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  return `${year} ${monthNames[parseInt(month) - 1]}`;
                }
              })()}
            </time>
            <span>·</span>
            <span>{readingTime} min read</span>
          </div>
        </motion.header>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="prose prose-lg max-w-none"
          style={{
            fontFamily: '"Noto Sans", -apple-system, sans-serif',
            fontFeatureSettings: '"liga" 1, "calt" 1',
            color: isDark ? 'rgba(255, 255, 255, 0.8)' : '#292524',
            lineHeight: '1.7'
          }}
        >
          <ReactMarkdown
            components={{
              p: ({ node, ...props }) => (
                <p className="mb-6 leading-[1.7]" style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }} {...props} />
              ),
              h2: ({ node, children, ...props }) => (
                <h2 className={`text-3xl font-light mt-12 mb-6 leading-[1.3] ${textColor}`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }} {...props}>{children}</h2>
              ),
              h3: ({ node, children, ...props }) => (
                <h3 className={`text-2xl font-light mt-8 mb-4 leading-[1.3] ${textColor}`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }} {...props}>{children}</h3>
              ),
              a: ({ node, children, ...props }) => (
                <a className={`${isDark ? 'text-white/80' : 'text-stone-800'} ${linkHover} underline underline-offset-2`} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
              ),
              strong: ({ node, ...props }) => (
                <strong className={`font-semibold ${isDark ? 'text-white' : 'text-stone-900'}`} {...props} />
              ),
              em: ({ node, ...props }) => (
                <em className="italic" {...props} />
              ),
              hr: ({ node, ...props }) => (
                <hr className={`my-12 ${borderColor}`} {...props} />
              ),
              code: ({ node, inline, ...props }) => (
                inline ? (
                  <code className={`${isDark ? 'bg-white/10 text-white' : 'bg-stone-200 text-stone-800'} px-1.5 py-0.5 rounded text-sm font-mono`} {...props} />
                ) : (
                  <code className="text-stone-300 font-mono" {...props} />
                )
              ),
              pre: ({ node, ...props }) => (
                <pre className="bg-stone-900 text-stone-300 p-4 rounded-lg overflow-x-auto my-6 text-sm max-w-[calc(100vw-3rem)] sm:max-w-none" {...props} />
              ),
              ul: ({ node, ...props }) => (
                <ul className="list-disc list-inside mb-6 space-y-2" style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }} {...props} />
              ),
              ol: ({ node, ...props }) => (
                <ol className="list-decimal list-inside mb-6 space-y-2" style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }} {...props} />
              ),
              blockquote: ({ node, ...props }) => (
                <blockquote className={`border-l-2 ${isDark ? 'border-white/10' : 'border-black/10'} pl-6 my-6 italic ${textMuted}`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }} {...props} />
              )
            }}
          >
            {post.content}
          </ReactMarkdown>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className={`mt-16 pt-12 border-t ${borderColor}`}
        >
          <div className="flex items-center gap-6">
            <a
              href={shareLinks.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className={`${textSubtle} ${linkHover} transition-colors text-sm`}
              aria-label="Share on Twitter"
            >
              Twitter
            </a>
            <a
              href={shareLinks.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className={`${textSubtle} ${linkHover} transition-colors text-sm`}
              aria-label="Share on LinkedIn"
            >
              LinkedIn
            </a>
            <button
              onClick={copyToClipboard}
              className={`${textSubtle} ${linkHover} transition-colors text-sm`}
              aria-label="Copy link"
            >
              Copy link
            </button>
          </div>

          {showShareToast && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className={`fixed bottom-6 right-6 ${isDark ? 'bg-white text-black' : 'bg-black text-white'} px-4 py-2 rounded text-sm`}
            >
              Link copied
            </motion.div>
          )}
        </motion.div>
      </div>
    </article>
  );
}

export default BlogPost;
